import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appVersionService } from '../services/appVersionService';
import AppVersionModal from './AppVersionModal';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { usePageContext } from '@/features/context/PageContext';

const AppVersionList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const queryClient = useQueryClient();
  const { setTitle, setBreadcrumb } = usePageContext();

  useEffect(() => {
    setTitle('Versions Mobile');
    setBreadcrumb([
      { label: 'Paramètres', url: '/settings' },
      { label: 'Versions Mobile' }
    ]);
  }, [setTitle, setBreadcrumb]);

  const { data: versions, isLoading, error } = useQuery({
    queryKey: ['app-versions'],
    queryFn: appVersionService.getAllVersions,
  });

  const updateVersionMutation = useMutation({
    mutationFn: appVersionService.updateVersion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-versions'] });
      toast.success('Version mise à jour avec succès');
      setIsModalOpen(false);
      setSelectedVersion(null);
      setSelectedPlatform(null);
    },
    onError: (error) => {
      toast.error('Erreur lors de la mise à jour de la version');
      console.error('Erreur:', error);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ platform, versionId, isActive }) => 
      appVersionService.toggleVersionStatus(platform, versionId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-versions'] });
      toast.success('Statut de la version modifié');
    },
    onError: () => {
      toast.error('Erreur lors du changement de statut');
    },
  });

  const handleEdit = (platform, version = null) => {
    setSelectedPlatform(platform);
    setSelectedVersion(version);
    setIsModalOpen(true);
  };

  const handleAdd = (platform) => {
    setSelectedPlatform(platform);
    setSelectedVersion(null);
    setIsModalOpen(true);
  };

  const handleToggleStatus = (platform, versionId, currentStatus) => {
    if (window.confirm(`Êtes-vous sûr de vouloir ${currentStatus ? 'désactiver' : 'activer'} cette version ?`)) {
      toggleStatusMutation.mutate({
        platform,
        versionId,
        isActive: !currentStatus
      });
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedVersion(null);
    setSelectedPlatform(null);
  };

  const handleModalSubmit = (formData) => {
    updateVersionMutation.mutate({
      platform: selectedPlatform,
      ...formData
    });
  };

  const getPlatformIcon = (platform) => {
    return platform === 'ios' ? DevicePhoneMobileIcon : ComputerDesktopIcon;
  };

  const getPlatformColor = (platform) => {
    return platform === 'ios' ? 'bg-primary' : 'bg-primary';
  };

  const getVersionStatus = (version) => {
    if (!version) return { icon: XCircleIcon, color: 'text-gray-400', text: 'Non configuré' };
    if (version.is_active) return { icon: CheckCircleIcon, color: 'text-green-500', text: 'Active' };
    return { icon: XCircleIcon, color: 'text-red-500', text: 'Inactive' };
  };



  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          Erreur lors du chargement des versions
        </div>
        <div className="text-sm text-gray-500">
          {error.message}
        </div>
      </div>
    );
  }

  const platforms = ['ios', 'android'];
  const platformLabels = { ios: 'iOS', android: 'Android' };

  return (
    <div className="space-y-6">
      

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platforms.map((platform) => {
          const version = versions?.[platform];
          const PlatformIcon = getPlatformIcon(platform);
          const status = getVersionStatus(version);
          const StatusIcon = status.icon;
          
          return (
            <div key={platform} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                {/* En-tête de la plateforme */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${getPlatformColor(platform)} text-white`}>
                      <PlatformIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {platformLabels[platform]}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <StatusIcon className={`h-4 w-4 ${status.color}`} />
                        <span className={`text-sm ${status.color}`}>
                          {status.text}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                   
                    {version && (
                      <button
                        onClick={() => handleEdit(platform, version)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500 text-white text-sm rounded-md  transition-colors"
                        title="Modifier"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Modifier
                      </button>
                    )}
                  </div>
                </div>

                {/* Informations de version */}
                {version ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase">
                          Version actuelle
                        </label>
                        <div className="text-lg font-semibold text-gray-900">
                          v{version.latest_version}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase">
                          Version minimale
                        </label>
                        <div className="text-lg font-semibold text-gray-900">
                          v{version.min_version}
                        </div>
                      </div>
                    </div>

                    {/* Indicateurs de mise à jour */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                      <div className="flex items-center space-x-2">
                        {version.force_update ? (
                          <>
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                            <span className="text-sm font-medium text-red-700">
                              Mise à jour forcée
                            </span>
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            <span className="text-sm font-medium text-green-700">
                              Mise à jour optionnelle
                            </span>
                          </>
                        )}
                      </div>
                      
                      <button
                        onClick={() => handleToggleStatus(platform, version.id, version.is_active)}
                        disabled={toggleStatusMutation.isPending}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          version.is_active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        } disabled:opacity-50`}
                      >
                        {version.is_active ? 'Actif' : 'Inactif'}
                      </button>
                    </div>

                    {/* Message de version */}
                    {version.message && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase">
                          Message aux utilisateurs
                        </label>
                        <div className="mt-1 p-2 bg-blue-50 rounded-md">
                          <p className="text-sm text-blue-800">{version.message}</p>
                        </div>
                      </div>
                    )}

                    {/* Date de création */}
                    {version.created_at && (
                      <div className="text-xs text-gray-500">
                        Créé le {new Date(version.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <PlatformIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">
                      Aucune version configurée pour {platformLabels[platform]}
                    </p>
                    <button
                      onClick={() => handleAdd(platform)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Configurer la première version
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AppVersionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        platform={selectedPlatform}
        version={selectedVersion}
        isLoading={updateVersionMutation.isPending}
      />
    </div>
  );
};

export default AppVersionList;